import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { rawCustomerAttributes } from '../types/models';
import { findSessionStorageByValue } from '@/composables/sessionStorageHelpers';
import { decodeHtmlEntities } from '@/composables/stringHelpers';

type ShippingAddress = NonNullable<rawCustomerAttributes['recentOrders'][number]['shipping']['address']>;

const decodeShippingAddress = (address: ShippingAddress): ShippingAddress =>
  Object.fromEntries(
    Object.entries(address).map(([key, value]) => [key, decodeHtmlEntities(value)]),
  ) as ShippingAddress;

export const useAccountStore = defineStore('accountStore', () => {
  const rawCustomerAttributes = ref<rawCustomerAttributes>({
    attributes: {
      loginType: '',
    },
    profile: {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    recentOrders: [],
    tags: [],
  });
  const setRawCustomerAttributes = (customerObject: rawCustomerAttributes) => {
    rawCustomerAttributes.value = {
      ...customerObject,
      recentOrders: customerObject.recentOrders.map((order) => ({
        ...order,
        shipping: {
          ...order.shipping,
          method: decodeHtmlEntities(order.shipping.method),
          address: order.shipping.address && decodeShippingAddress(order.shipping.address),
        },
      })),
    };
  };

  const oxiLoginIframe = ref('');
  const setOxiLoginIframe = (value: string) => (oxiLoginIframe.value = value);

  const profile = computed(() => rawCustomerAttributes.value.profile);
  const recentOrders = computed(() => rawCustomerAttributes.value.recentOrders);
  const tags = computed(() => rawCustomerAttributes.value.tags);

  const loginType = computed<null | string>(() => {
    const isLoggedIn = !!profile.value.email;
    if (!isLoggedIn) return null;
    const hasOxiSocialSessionStorage = findSessionStorageByValue('social-login.oxiapps');
    return hasOxiSocialSessionStorage === null ? 'form' : 'sso';
  });
  const attributes = computed(() => ({
    isLoggedIn: !!profile.value.email,
    loginType: loginType.value,
  }));

  return {
    setRawCustomerAttributes,
    oxiLoginIframe,
    setOxiLoginIframe,
    attributes,
    profile,
    recentOrders,
    tags,
  };
});
